import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getDescriptionForPath } from '../../config/configEntry';
import {fetchDiscoveryIcons, uniqueIconsFromButtons,} from '../../services/admin/discoveryIconsApi';
import {loadLocalDiscoveryIcons,removeLocalDiscoveryIcon,saveLocalDiscoveryIcon,} from './discoveryIconsLocalStorage';
import { emptyDiscoveryButton } from './discoveryButtonsValidation';
import classes from './DiscoveryButtonsEditor.module.css';
import panelClasses from './AdminSettingsPanel.module.css';

function PenIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 20h4l10.5-10.5a1.5 1.5 0 0 0-4.24-4.24L4 15.76V20z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M13.5 6.5l4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M10 11v6M14 11v6M6 7l1 12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-12"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DiscoveryButtonRow({ button, onEdit, onRemove }) {
  const displayText = (button.text ?? '').trim() || 'ללא טקסט';
  const isPlaceholder = !(button.text ?? '').trim();

  return (
    <div className={classes.buttonRow}>
      <span
        className={`${classes.buttonRowText} ${isPlaceholder ? classes.buttonRowTextPlaceholder : ''}`}
      >
        {displayText}
      </span>
      <div className={classes.buttonRowActions}>
        <button
          type="button"
          className={classes.iconBtn}
          onClick={onEdit}
          aria-label="עריכת כפתור"
        >
          <PenIcon />
        </button>
        <button
          type="button"
          className={`${classes.iconBtn} ${classes.iconBtnDanger}`}
          onClick={onRemove}
          aria-label="הסרת כפתור"
        >
          <TrashIcon />
        </button>
      </div>
    </div>
  );
}

function DiscoveryButtonEditForm({
  index,
  button,
  icons,
  iconsLoading,
  selectedIconMeta,
  bucketKey,
  labelIcon,
  labelText,
  labelMessage,
  onPatch,
  onIconsChange,
}) {
  const fileInputRef = useRef(null);
  const [uploadError, setUploadError] = useState('');

  const isSelectedIconLocal = useMemo(() => {
    if (!button.icon) return false;
    const meta = selectedIconMeta.get(button.icon);
    return meta?.source === 'local';
  }, [button.icon, selectedIconMeta]);

  const onUploadClick = () => {
    setUploadError('');
    fileInputRef.current?.click();
  };

  const onUploadFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setUploadError('');

    const name = String(file.name || '').toLowerCase();
    if (!name.endsWith('.svg') && file.type !== 'image/svg+xml') {
      setUploadError('אפשר להעלות רק קובץ SVG');
      return;
    }

    const maxBytes = 200 * 1024;
    if (typeof file.size === 'number' && file.size > maxBytes) {
      setUploadError('הקובץ גדול מדי (מקסימום 200KB)');
      return;
    }

    let svgText = '';
    try {
      svgText = await readFileAsText(file);
    } catch {
      setUploadError('נכשלה קריאת הקובץ');
      return;
    }

    const normalizedSvg = svgText.replace(/^\uFEFF/, '').trim();
    if (!/<svg[\s>]/i.test(normalizedSvg)) {
      setUploadError('הקובץ לא נראה כמו SVG תקין');
      return;
    }

    const dataUrl = svgToDataUrl(normalizedSvg);
    const fileName = String(file.name || 'icon').replace(/\.svg$/i, '');

    const saved = saveLocalDiscoveryIcon(bucketKey, { url: dataUrl, label: fileName, fileName });
    if (!saved) {
      setUploadError('לא ניתן לשמור (ייתכן ש-localStorage מלא)');
      return;
    }

    const nextIcons = mergeIconLists(icons, [saved]);
    onIconsChange(nextIcons);
  };

  const onRemoveLocal = () => {
    if (!button.icon) return;
    const meta = selectedIconMeta.get(button.icon);
    if (!meta || meta.source !== 'local') return;

    removeLocalDiscoveryIcon(bucketKey, meta.id);
    const nextIcons = icons.filter((it) => it.url !== meta.url);
    onIconsChange(nextIcons);
  };

  return (
    <>
      <div className={classes.iconPicker}>
        <label className={panelClasses.label}>{labelIcon}</label>
        {button.icon ? (
          <div className={classes.iconPreviewRow}>
            <img src={button.icon} alt="" />
            <span>{selectedIconMeta.get(button.icon)?.label || button.icon}</span>
          </div>
        ) : null}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className={panelClasses.btn} onClick={onUploadClick}>
            העלה אייקון (SVG)
          </button>
          {isSelectedIconLocal ? (
            <button
              type="button"
              className={panelClasses.btn}
              onClick={onRemoveLocal}
              title="מוחק רק מהספרייה המקומית"
            >
              הסר מהספרייה המקומית
            </button>
          ) : null}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/svg+xml,.svg"
            style={{ display: 'none' }}
            onChange={onUploadFile}
          />
        </div>
        {uploadError ? <p className={classes.hintError}>{uploadError}</p> : null}
        {!iconsLoading && icons.length > 0 ? (
          <div className={classes.iconGrid} role="listbox" aria-label={labelIcon}>
            {icons.map((item) => {
              const selected = button.icon === item.url;
              return (
                <button
                  key={item.url}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`${classes.iconOption} ${selected ? classes.iconOptionSelected : ''}`}
                  title={item.label}
                  onClick={() => onPatch({ icon: item.url })}
                >
                  <img src={item.url} alt={item.label} />
                </button>
              );
            })}
          </div>
        ) : null}
      </div>

      <div className={panelClasses.field}>
        <label className={panelClasses.label} htmlFor={`discovery-text-${index}`}>
          {labelText}
        </label>
        <input
          id={`discovery-text-${index}`}
          className={panelClasses.textInput}
          value={button.text ?? ''}
          onChange={(e) => onPatch({ text: e.target.value })}
        />
      </div>

      <div className={panelClasses.field}>
        <label className={panelClasses.label} htmlFor={`discovery-message-${index}`}>
          {labelMessage}
        </label>
        <textarea
          id={`discovery-message-${index}`}
          className={`${panelClasses.textarea} ${classes.messageInput}`}
          value={button.message ?? ''}
          onChange={(e) => onPatch({ message: e.target.value })}
        />
      </div>
    </>
  );
}

function DiscoveryButtonEditModal({
  index,
  button,
  icons,
  iconsLoading,
  selectedIconMeta,
  bucketKey,
  labelIcon,
  labelText,
  labelMessage,
  onPatch,
  onIconsChange,
  onClose,
  modalContainer,
}) {
  const panelRef = useRef(null);
  const titleId = `discovery-modal-title-${index}`;

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  useEffect(() => {
    const focusable = panelRef.current?.querySelector(
      'button:not([aria-label="סגור"]), input, textarea'
    );
    focusable?.focus();
  }, []);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!modalContainer) {
    return null;
  }

  return createPortal(
    <div
      className={classes.modalBackdrop}
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        ref={panelRef}
        className={classes.modalPanel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={classes.modalHeader}>
          <p id={titleId} className={classes.modalTitle}>
            כפתור {index + 1}
          </p>
          <button
            type="button"
            className={classes.modalClose}
            onClick={onClose}
            aria-label="סגור"
          >
            ×
          </button>
        </div>

        <div className={classes.modalBody}>
          <DiscoveryButtonEditForm
            index={index}
            button={button}
            icons={icons}
            iconsLoading={iconsLoading}
            selectedIconMeta={selectedIconMeta}
            bucketKey={bucketKey}
            labelIcon={labelIcon}
            labelText={labelText}
            labelMessage={labelMessage}
            onPatch={onPatch}
            onIconsChange={onIconsChange}
          />
        </div>
      </div>
    </div>,
    modalContainer
  );
}

export default function DiscoveryButtonsEditor({
  botName,
  draft,
  buttons,
  onChange,
  descriptions,
  modalContainer,
}) {
  const [icons, setIcons] = useState([]);
  const [iconsLoading, setIconsLoading] = useState(true);
  const [iconsHint, setIconsHint] = useState('');
  const [bucketKey, setBucketKey] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setIconsLoading(true);
    setIconsHint('');

    fetchDiscoveryIcons(botName, draft).then(({ icons: loaded, source, bucketKey }) => {
      if (cancelled) return;

      const fromButtons = uniqueIconsFromButtons(buttons);
      const fromLocal = loadLocalDiscoveryIcons(bucketKey);
      const merged = mergeIconLists(loaded, fromButtons, fromLocal);

      setIcons(merged);
      setIconsLoading(false);
      setBucketKey(bucketKey || '');

      if (merged.length === 0) {
        setIconsHint(`לא נמצאו אייקונים (bucket: ${bucketKey})`);
      } else if (source === 'fallback') {
        setIconsHint(`רשימת אייקונים מקומית (${bucketKey})`);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [botName, draft, buttons]);

  const labelText = getDescriptionForPath(
    descriptions,
    'טקסט על הכפתור',
    'discoveryButton.text'
  );
  const labelMessage = getDescriptionForPath(
    descriptions,
    'הודעה לבוט',
    'discoveryButton.message'
  );
  const labelIcon = getDescriptionForPath(
    descriptions,
    'אייקון',
    'discoveryButton.icon'
  );

  const defaultIcon = icons[0]?.url ?? '';
  const canAddButton = !iconsLoading && Boolean(defaultIcon);

  const updateButton = useCallback(
    (index, patch) => {
      const next = buttons.map((btn, i) => (i === index ? { ...btn, ...patch } : btn));
      onChange(next);
    },
    [buttons, onChange]
  );

  const addButton = () => {
    if (!canAddButton) return;
    const newIndex = buttons.length;
    onChange([...buttons, emptyDiscoveryButton(defaultIcon)]);
    setEditingIndex(newIndex);
  };

  const removeButton = (index) => {
    onChange(buttons.filter((_, i) => i !== index));
    setEditingIndex((current) => {
      if (current === null) return null;
      if (current === index) return null;
      if (current > index) return current - 1;
      return current;
    });
  };

  const closeModal = useCallback(() => {
    setEditingIndex(null);
  }, []);

  const selectedIconMeta = useMemo(() => {
    const map = new Map(icons.map((item) => [item.url, item]));
    return map;
  }, [icons]);

  const editingButton =
    editingIndex !== null && editingIndex >= 0 && editingIndex < buttons.length
      ? buttons[editingIndex]
      : null;

  useEffect(() => {
    if (editingIndex !== null && editingIndex >= buttons.length) {
      setEditingIndex(null);
    }
  }, [buttons.length, editingIndex]);

  return (
    <section className={panelClasses.field}>
      <div className={classes.sectionHeader}>
        <div className={panelClasses.sectionTitle} style={{ margin: 0 }}>
          כפתורי Discovery
        </div>
        <button
          type="button"
          className={panelClasses.btn}
          onClick={addButton}
          disabled={!canAddButton}
          title={!canAddButton ? 'ממתין לטעינת אייקונים' : undefined}
        >
          + הוסף כפתור
        </button>
      </div>

      {iconsLoading ? <p className={classes.loading}>טוען אייקונים...</p> : null}
      {!iconsLoading && !canAddButton ? (
        <p className={classes.hint}>לא ניתן להוסיף כפתור לפני שנטענו אייקונים</p>
      ) : null}
      {!iconsLoading && iconsHint ? <p className={classes.hint}>{iconsHint}</p> : null}

      {buttons.length === 0 ? (
        <p className={classes.hint}>אין כפתורים. לחצי על &quot;הוסף כפתור&quot; להתחלה.</p>
      ) : null}

      {buttons.length > 0 ? (
        <div className={classes.buttonList}>
          {buttons.map((button, index) => (
            <DiscoveryButtonRow
              key={`discovery-btn-${index}`}
              button={button}
              onEdit={() => setEditingIndex(index)}
              onRemove={() => removeButton(index)}
            />
          ))}
        </div>
      ) : null}

      {editingButton !== null && editingIndex !== null ? (
        <DiscoveryButtonEditModal
          index={editingIndex}
          button={editingButton}
          icons={icons}
          iconsLoading={iconsLoading}
          selectedIconMeta={selectedIconMeta}
          bucketKey={bucketKey}
          labelIcon={labelIcon}
          labelText={labelText}
          labelMessage={labelMessage}
          onPatch={(patch) => updateButton(editingIndex, patch)}
          onIconsChange={setIcons}
          onClose={closeModal}
          modalContainer={modalContainer}
        />
      ) : null}
    </section>
  );
}

function mergeIconLists(...lists) {
  const seen = new Set();
  const out = [];

  lists.flat().forEach((item) => {
    if (!item?.url || seen.has(item.url)) return;
    seen.add(item.url);
    out.push(item);
  });

  return out;
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read failed'));
    reader.onload = () => resolve(String(reader.result || ''));
    reader.readAsText(file);
  });
}

function svgToDataUrl(svgText) {
  return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgText)))}`;
}
